import axiosInstance from '../utils/axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config/api.config';

// Don't need to define API_URL since axiosInstance handles it
// Don't need axiosConfig since axiosInstance has the headers configured

/**
 * Service for managing customers
 */
export const customerService = {
  /**
   * Get all customers with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @param {string} params.includeInactive - Include inactive customers (default: false)
   * @returns {Promise<Object>} Paginated customer data
   */
  getAllCustomers: async (params = { page: 0, size: 10, sort: 'name', direction: 'ASC' }) => {
    try {
      const response = await axiosInstance.get('/customers', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching customers:', error.response || error);
      throw error;
    }
  },

  /**
   * Get customers by company ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Array>} List of customers
   */
  getCustomersByCompany: async (companyId) => {
    try {
      if (!companyId) {
        throw new Error('Company ID is required');
      }
      const response = await axiosInstance.get(`/customers/company/${companyId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching customers by company:', error.response || error);
      throw error;
    }
  },

  /**
   * Get a customer by ID
   * @param {number} id - Customer ID
   * @returns {Promise<Object>} Customer details
   */
  getCustomerById: async (id) => {
    try {
      const response = await axiosInstance.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error.response || error);
      throw error;
    }
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} Created customer
   */
  createCustomer: async (customerData) => {
    try {
      const response = await axiosInstance.post('/customers', customerData);
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error.response || error);
      throw error;
    }
  },

  /**
   * Update a customer
   * @param {number} id - Customer ID
   * @param {Object} customerData - Updated customer data
   * @returns {Promise<Object>} Updated customer
   */
  updateCustomer: async (id, customerData) => {
    try {
      const response = await axiosInstance.put(`/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error.response || error);
      throw error;
    }
  },

  /**
   * Toggle customer status (Active/Inactive) - replaces permanent delete
   * @param {number} id - Customer ID
   * @returns {Promise<Object>} Updated customer with new status
   */
  deleteCustomer: async (id) => {
    try {
      const response = await axiosInstance.delete(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error toggling customer status:', error.response || error);
      throw error;
    }
  },

  /**
   * Toggle customer status explicitly
   * @param {number} id - Customer ID
   * @returns {Promise<Object>} Updated customer with new status
   */
  toggleCustomerStatus: async (id) => {
    try {
      const response = await axiosInstance.patch(`/customers/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('Error toggling customer status:', error.response || error);
      throw error;
    }
  },

  /**
   * Redeem loyalty points (Gift or Cash) – deducts points and records in loyalty ledger.
   * @param {number} id - Customer ID
   * @param {number} pointsToRedeem - Points to redeem
   * @param {string} redemptionType - "GIFT" or "CASH"
   * @returns {Promise<Object>} Updated customer (with new loyaltyPoints)
   */
  redeemLoyaltyPoints: async (id, pointsToRedeem, redemptionType) => {
    try {
      const response = await axiosInstance.post(`/customers/${id}/loyalty-redeem`, {
        pointsToRedeem: Number(pointsToRedeem) || 0,
        redemptionType: redemptionType || 'GIFT'
      });
      return response.data;
    } catch (error) {
      console.error('Error redeeming loyalty points:', error.response || error);
      throw error;
    }
  }
}; 